import React from "react";
import { SparkAreaChart } from "@/components/ui/tremor-charts/SparkChart";
import { DataGraphResponse } from "../../../types";
import DiagonalShimmer from "@/components/common/loaders/DiagonalShimmer";
import { cn } from "@/lib/utils";
import {
  SqTooltip,
  SqTooltipContent,
  SqTooltipProvider,
  SqTooltipTrigger,
} from "@/components/squircle-ui/tooltip";

interface SparkGraphProps {
  onClick: () => void;
  graphData: DataGraphResponse | undefined;
  loading: boolean;
  metricKey: string;
  sectionId: string;
  change: number;
  className?: string;
}

const extractMetricTimeSeries = (
  graphData: DataGraphResponse | undefined,
  metricKey: string,
) => {
  return (
    graphData?.data?.data_points.map((point) => {
      let yValue: number | string | undefined = undefined;
      if (
        point &&
        typeof point === "object" &&
        point?.aggregated &&
        typeof point?.aggregated === "object" &&
        metricKey in point.aggregated
      ) {
        yValue = (point.aggregated as { [key: string]: number | string })[metricKey];
      }
      return {
        x: point.x,
        y: yValue,
      };
    }) ?? []
  );
};

const SparkGraph: React.FC<SparkGraphProps> = ({
  graphData,
  loading,
  metricKey,
  change,
  className,
  onClick,
}) => {
  const sparkData = extractMetricTimeSeries(graphData, metricKey);

  return (
    <div className={cn(className, loading && "rounded-sm overflow-hidden")}>
      <SqTooltipProvider>
        <SqTooltip>
          {loading ? (
            <DiagonalShimmer
              colors={["#F6F6F666", "#BFBFBF44", "#FFFFFFCC"]}
              duration={5}
              className={`relative ${className}`}
            />
          ) : (
            <div className="relative">
              <SparkAreaChart
                data={sparkData}
                index="x"
                categories={["y"]}
                colors={[change >= 0 ? "emerald" : "red"]}
                className={className}
              />
              <SqTooltipTrigger>
                <div
                  className="absolute inset-0 cursor-pointer transition-colors"
                  onClick={onClick}
                />
              </SqTooltipTrigger>
              <SqTooltipContent className="!bg-black/80 squircle-mask">
                View full graph
              </SqTooltipContent>
            </div>
          )}
        </SqTooltip>
      </SqTooltipProvider>
    </div>
  );
};

export default SparkGraph;
